import React, { useMemo, useState } from 'react'
import { Show } from '@refinedev/antd'
import { useShow, useNavigation, useOne, useList } from '@refinedev/core'
import { App, Button, Space } from 'antd'
import type { Tables } from '@muaa/shared'
import { brand } from '../../theme/brand'
import { Pill, surface } from '../../theme/listkit'
import { renderStatus } from '../../theme/columns'
import { supabaseClient } from '../../lib/supabaseClient'
import { groupMeetupsAdminErrorMessage } from './adminErrors'
import { formatMeetupStart, meetupToEventDraft } from './format'

type GroupMeetup = Tables<'group_meetups'>
type Group = Tables<'groups'>
type GroupMeetupRsvp = Tables<'group_meetup_rsvps'>

const FORMAT_LABEL: Record<string, string> = {
  in_person: 'In person',
  virtual: 'Video',
  hybrid: 'In person + video',
}

const labelStyle: React.CSSProperties = {
  fontFamily: brand.fSans,
  fontWeight: 700,
  fontSize: 11,
  letterSpacing: '.09em',
  textTransform: 'uppercase',
  color: brand.gold,
  marginBottom: 4,
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={labelStyle}>{label}</div>
      <div style={{ fontFamily: brand.fSans, fontWeight: 500, fontSize: 13.5, color: brand.ink }}>{children}</div>
    </div>
  )
}

/**
 * Meetup detail — read-only summary plus two admin actions: "Cancel meetup"
 * (the same `cancel_meetup` RPC the organizer's own Cancel button calls —
 * one cancellation entry point, reached from two callers) and "Promote to
 * Event", which inserts a minimally-filled draft `events` row
 * (`meetupToEventDraft`, format.ts) and opens it on the Events Edit page for
 * the admin to finish (add a hero image, ticket link, etc. — fields
 * `group_meetups` has no equivalent of).
 *
 * Completion pass (progress-p3-server.md's own Deviation 8: "the admin Meetup
 * Show page does not (yet) surface meetup_rsvp_summary's RSVP counts", out of
 * that task's stated scope): adds an "RSVPs" field, a plain
 * `group_meetup_rsvps` read filtered to this meetup's id — same RLS the List
 * page's counts already rely on (`group_meetup_rsvps: admin write` is a
 * `for all` policy, so it covers select for an admin too) — rather than
 * calling the member-facing `meetup_rsvp_summary` RPC, which would be an
 * unnecessary indirection for a caller that can already read the raw rows.
 */
export function GroupMeetupShow() {
  const { query } = useShow<GroupMeetup>()
  const { data, isLoading } = query
  const record = data?.data
  const { edit } = useNavigation()

  const { query: groupQuery } = useOne<Group>({
    resource: 'groups',
    id: record?.group_id ?? '',
    queryOptions: { enabled: Boolean(record?.group_id) },
  })
  const group = groupQuery.data?.data

  const { result: rsvpResult } = useList<GroupMeetupRsvp>({
    resource: 'group_meetup_rsvps',
    filters: record ? [{ field: 'meetup_id', operator: 'eq', value: record.id }] : [],
    pagination: { mode: 'off' },
    queryOptions: { enabled: Boolean(record) },
  })
  const rsvpCounts = useMemo(() => {
    const out = { going: 0, maybe: 0, not_going: 0 }
    for (const r of (rsvpResult?.data ?? []) as GroupMeetupRsvp[]) {
      if (r.response === 'going') out.going += 1
      else if (r.response === 'maybe') out.maybe += 1
      else if (r.response === 'not_going') out.not_going += 1
    }
    return out
  }, [rsvpResult])

  const { message, modal } = App.useApp()
  const [cancelling, setCancelling] = useState(false)
  const [promoting, setPromoting] = useState(false)

  async function cancelMeetup() {
    if (!record) return
    setCancelling(true)
    try {
      const { error } = await supabaseClient.rpc('cancel_meetup', { p_meetup: record.id })
      if (error) throw error
      message.success('Meetup cancelled')
      query.refetch()
    } catch (e) {
      message.error(groupMeetupsAdminErrorMessage(e))
    } finally {
      setCancelling(false)
    }
  }

  function confirmCancel() {
    if (!record) return
    modal.confirm({
      title: 'Cancel this meetup?',
      content: 'Everyone who RSVP’d will be told.',
      okText: 'Cancel meetup',
      okButtonProps: { danger: true },
      onOk: cancelMeetup,
    })
  }

  async function promoteToEvent() {
    if (!record) return
    setPromoting(true)
    try {
      const draft = meetupToEventDraft(record)
      const { data: created, error } = await supabaseClient.from('events').insert(draft).select('id').single()
      if (error) throw error
      message.success('Draft event created — finish it up below')
      edit('events', (created as { id: string }).id)
    } catch (e) {
      message.error(groupMeetupsAdminErrorMessage(e))
    } finally {
      setPromoting(false)
    }
  }

  const canCancel = record?.status === 'scheduled'

  return (
    <Show isLoading={isLoading} title={false} breadcrumb={false} headerButtons={() => null}>
      <div
        style={{
          background: `linear-gradient(135deg, ${brand.navy} 0%, ${brand.jaguar} 100%)`,
          borderRadius: 14,
          padding: '22px 24px',
          marginBottom: 18,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 18,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <div style={{ ...labelStyle, color: brand.gold }}>{group?.name ?? 'Meetup'}</div>
          <div style={{ fontFamily: brand.fDisplay, fontWeight: 700, fontSize: 24, color: '#fff' }}>
            {record?.title ?? '—'}
          </div>
          <Space size={8} style={{ marginTop: 10 }} wrap>
            {record && <Pill tone={record.status === 'cancelled' ? 'red' : 'green'}>{record.status}</Pill>}
            {record && <Pill tone="jaguar">{FORMAT_LABEL[record.format] ?? record.format}</Pill>}
          </Space>
        </div>
        {record && (
          <Space wrap>
            {canCancel && (
              <Button data-testid="meetup-cancel" danger loading={cancelling} disabled={promoting} onClick={confirmCancel}>
                Cancel meetup
              </Button>
            )}
            <Button
              data-testid="meetup-promote"
              type="primary"
              loading={promoting}
              disabled={cancelling}
              onClick={promoteToEvent}
            >
              Promote to Event
            </Button>
          </Space>
        )}
      </div>

      {record && (
        <div style={{ ...surface, padding: '20px 24px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 8 }}>
            <Field label="Starts">{formatMeetupStart(record)}</Field>
            <Field label="Status">{renderStatus(record.status)}</Field>
            <Field label="Venue">{record.venue_name ?? '—'}</Field>
            <Field label="Address">{record.address ?? '—'}</Field>
            <Field label="Capacity">{record.capacity ?? 'Unlimited'}</Field>
            <Field label="Time zone">{record.tz}</Field>
            <Field label="RSVPs">
              {`${rsvpCounts.going.toLocaleString()} going · ${rsvpCounts.maybe.toLocaleString()} maybe · ${rsvpCounts.not_going.toLocaleString()} can’t`}
            </Field>
          </div>
          {record.details && <Field label="Details">{record.details}</Field>}
        </div>
      )}
    </Show>
  )
}
